import {
  Divider,
  DropdownButton,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { DashboardLayout, FavoriteDashboardSortBy } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { Fragment, memo, useMemo, useState } from 'react';

import { useDashboardLandingPage } from 'app/views/layout/views/dashboard/utils/useDashboardLandingPage';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import {
  favoriteSortByPreferenceAssets,
  layoutPreferenceAssets,
} from '../../../../../../assets/dashboardPreferencesAssets';

import styles from './favoriteDashboardPreferencesControl.module.scss';

export const FavoriteDashboardPreferencesControl = memo(
  function FavoriteDashboardPreferencesControl(): JSX.Element {
    const { params, setParam, unsetParam } = useAppSearchParams(
      '/dashboard/favorites',
    );
    const { setLandingPage } = useDashboardLandingPage();
    const storage = useServerStorage('user');

    const currentLayout = storage.data.dashboard?.layout ?? 'default';
    const [isOpen, setIsOpen] = useState(false);

    const handleLayoutChange = (layout: DashboardLayout) => {
      storage.set({
        dashboard: (draft) => {
          draft.layout = layout;
        },
      });

      ampli.dashboardLayoutChanged({ layout });
    };

    const handleSortByChange = (value: FavoriteDashboardSortBy) => {
      if (value === params.sort_by) {
        unsetParam('sort_by');
      } else {
        setParam('sort_by', value);
      }

      setLandingPage({
        type: 'last_visited',
        url: {
          path: '/dashboard/favorites',
          params: {
            ...(value !== params.sort_by ? { search: { sort_by: value } } : {}),
          },
        },
      });
    };

    const currentSort = useMemo(
      (): FavoriteDashboardSortBy => params.sort_by ?? 'name:asc',
      [params.sort_by],
    );

    const selectedValues: [FavoriteDashboardSortBy, DashboardLayout] = useMemo(
      () => [currentSort, currentLayout],
      [currentSort, currentLayout],
    );

    return (
      <DropdownButton
        show={isOpen}
        onToggle={setIsOpen}
        icon="preferences"
        buttonProps={{
          variant: 'secondary',
        }}
        fixed
        dropdown={
          <>
            <SelectionList
              className={styles.list}
              isActive={false}
              selectedValues={selectedValues}
              defaultActiveItemIndex={0}
            >
              <Divider padding="300 300 200">
                <Text.Paragraph as="span" color="tertiary">
                  Sort by
                </Text.Paragraph>
              </Divider>

              {favoriteSortByPreferenceAssets.map(({ value, label, image }) => (
                <Fragment key={value}>
                  <SelectionList.Item
                    value={value}
                    contentClassName={styles.item}
                    onClick={() => handleSortByChange(value)}
                  >
                    {label} {image}
                  </SelectionList.Item>
                </Fragment>
              ))}

              <Divider padding="200 300">
                <Text.Paragraph as="span" color="tertiary">
                  Layout
                </Text.Paragraph>
              </Divider>

              {layoutPreferenceAssets.map(({ value, label, image }) => (
                <Fragment key={value}>
                  <SelectionList.Item
                    value={value}
                    contentClassName={styles.item}
                    onClick={() => handleLayoutChange(value)}
                  >
                    {label} {image}
                  </SelectionList.Item>
                </Fragment>
              ))}
            </SelectionList>
          </>
        }
      />
    );
  },
);
